import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Save, Check } from "lucide-react";
import api from "@/lib/api";
import { Btn, Card, Pill, Inp, errMsg } from "@/components/datacenter/ui";

export default function MappingStep({ job, meta, base, sample, admin, onChanged }) {
  const fields = meta.entities[job.entity].fields;
  const [map, setMap] = useState(() => Object.fromEntries(job.mapping.map((m) => [m.column, m])));
  const [busy, setBusy] = useState("");
  const [tplName, setTplName] = useState("");
  const groups = [...new Set(fields.map((f) => f.group))];
  const used = Object.values(map).map((m) => m.field).filter(Boolean);
  const set = (col, field) => setMap((m) => ({ ...m, [col]: { ...m[col], field: field || null, confidence: field ? 100 : 0, by: "admin" } }));
  const apply = async () => {
    setBusy("apply");
    try { await api.put(`${base}/${job.id}/mapping`, { mapping: Object.values(map) }); toast.success("Mapping applied — re-validating rows"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const aiMap = async () => {
    setBusy("ai");
    try { const r = await api.post(`${base}/${job.id}/ai-map`); setMap(Object.fromEntries(r.data.mapping.map((m) => [m.column, m]))); toast.success("AI mapping suggested — review and apply"); }
    catch (e) { toast.error(errMsg(e, "AI mapping unavailable")); } finally { setBusy(""); }
  };
  const saveTpl = async () => {
    if (!tplName.trim()) return toast.error("Name the template");
    setBusy("tpl");
    try { await api.post(`${base}/${job.id}/save-template`, { name: tplName }); toast.success("Mapping template saved — similar files will use it automatically"); setTplName(""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <Card tid="dc-mapping-step">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-display text-lg font-semibold text-[#071b22]">Map fields</div>
          <div className="text-xs text-[#252A34]/60">{job.template_name ? `Auto-applied saved template “${job.template_name}”. ` : ""}Column names don't need to match — pick the Gulf Coast Work field for each.</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {admin && <Btn kind="ghost" onClick={aiMap} busy={busy === "ai"} tid="dc-ai-map"><Sparkles className="h-4 w-4" /> AI suggest</Btn>}
          <Btn onClick={apply} busy={busy === "apply"} tid="dc-apply-mapping"><Check className="h-4 w-4" /> Apply mapping</Btn>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dc-mapping-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#252A34]/50"><th className="py-2 pr-3">Detected column</th><th className="pr-3">Sample</th><th className="pr-3">Gulf Coast Work field</th><th>Confidence</th></tr></thead>
          <tbody>
            {job.headers.map((h, i) => {
              const m = map[h] || {};
              return (
                <tr key={h} className="border-t border-slate-100" data-testid={`dc-map-row-${i}`}>
                  <td className="py-2 pr-3 font-medium text-[#071b22]">{h}</td>
                  <td className="pr-3 text-xs text-[#252A34]/60 max-w-[220px] truncate">{sample?.[h] || "—"}</td>
                  <td className="pr-3">
                    <select value={m.field || ""} onChange={(e) => set(h, e.target.value)} className="rounded-xl border border-slate-200 px-2 py-1.5 text-sm w-full min-w-[200px]" data-testid={`dc-map-select-${i}`}>
                      <option value="">— Don't import —</option>
                      {groups.map((g) => (
                        <optgroup key={g} label={g}>
                          {fields.filter((f) => f.group === g).map((f) => <option key={f.key} value={f.key} disabled={used.includes(f.key) && m.field !== f.key}>{f.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td>{m.field ? <Pill s={m.confidence >= 90 ? "ready" : m.confidence >= 75 ? "warning" : "review"}>{m.confidence}% {m.by === "ai" ? "AI" : m.by === "template" ? "template" : ""}</Pill> : <span className="text-xs text-[#252A34]/40">unmapped</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {admin && (
        <div className="mt-4 flex gap-2 items-center flex-wrap">
          <Inp value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Save mapping as… e.g. Angi Provider Export" className="flex-1 min-w-[240px]" data-testid="dc-template-name" />
          <Btn kind="ghost" onClick={saveTpl} busy={busy === "tpl"} tid="dc-save-template"><Save className="h-4 w-4" /> Save template</Btn>
        </div>
      )}
    </Card>
  );
}
